// ===== Pannellum 360° Viewer Component =====
// Uses vanilla pannellum library with proper equirectangular config

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import 'pannellum/build/pannellum.css';
import 'pannellum/build/pannellum.js';
import { 
  XMarkIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// CDN base
const CDN_BASE = 'https://media.honestlee.app';

// EV-242: the raw equirectangular originals are 20MB+ and exceed the mobile GPU
// MAX_TEXTURE_SIZE → the 360 hangs on the spinner / renders blank. Serve a
// 4096px-wide webp via the CDN resizer instead (~600KB, confirmed 200) — well
// within texture limits and still sharp for a phone 360 viewer.
// CON-1376 — TWO-STAGE 360. Stage 1 is a 6144px webp (~0.9MB) that starts almost
// instantly; stage 2 is the NATIVE-resolution webp (avg 11,904px, ~2.9MB) which
// is preloaded in the background and cross-faded in via a Pannellum scene swap,
// preserving yaw/pitch/hfov — so zooming actually reveals detail.
// The old single 4096 cap (EV-242) existed because raw 20MB+ originals blew past
// mobile MAX_TEXTURE_SIZE; we now pick per-device instead of capping everyone.
const getStage1Url = (s3Key) => {
  if (!s3Key) return '';
  return `${CDN_BASE}/r?key=${encodeURIComponent(s3Key)}&w=6144&f=webp&q=80`;
};
const getFullUrl = (s3Key) => {
  if (!s3Key) return '';
  return `${CDN_BASE}/r?key=${encodeURIComponent(s3Key)}&full=1&f=webp`;
};

// What this GPU can actually accept as one texture. Pannellum throws
// "This panorama is too big for your device" above it rather than degrading,
// so stage 2 is only attempted when the hardware reports enough headroom.
let cachedMaxTex = null;
const getMaxTextureSize = () => {
  if (cachedMaxTex !== null) return cachedMaxTex;
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
    cachedMaxTex = gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : 4096;
  } catch { cachedMaxTex = 4096; }
  return cachedMaxTex;
};

const Panellum360Viewer = ({
  images = [],
  initialIndex = 0,
  onClose,
  venueName = 'Venue',
  venueSlug = null,   // EV-291 — when set, enables the "Share link" (deep link) button
  initialHfov = 100,  // v69.868 — start zoom (higher = more zoomed OUT); admin-tunable from the gallery
  // CON-1376 — Pannellum feel, all admin-tunable (web_settings.pannellum_*).
  // touchPanSpeed was UNSET (= 1) and Patrick's S24 Ultra felt "way too slow";
  // pannellum scales drag-degrees by hfov, so zoomed-in panning crawls at 1.
  touchPanSpeed = 2.5,
  autoRotateSpeed = -2.5,
  minHfovSetting = 20,
  maxHfovSetting = 120,
  fadeMs = 400,
  progressiveFull = true,   // load native-res stage 2 in the background
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const pannellumContainerRef = useRef(null);
  // CON-1376 — guards the one-way stage-1 → stage-2 upgrade so a late-arriving
  // preload can't swap a viewer that has already moved to another panorama.
  const fullSwappedRef = useRef(false);
  const fullContainerRef = useRef(null);   // CON-1376 — stage-2 layer
  const fullViewerRef = useRef(null);

  const currentImage = images[currentIndex];
  const s3Key = currentImage?.s3Key;
  // v69.870 — when images carry their own venueName/venueSlug (the city gallery's
  // cross-venue 360 set), the header + share follow the CURRENT image; the venue
  // 360 page keeps passing a single venueName as before.
  const curVenueName = currentImage?.venueName || venueName;
  const curVenueSlug = currentImage?.venueSlug ?? venueSlug;
  
  // Stage 1 — the fast one. Stage 2 is fetched later, only if the GPU can take it.
  const imageUrl = s3Key ? getStage1Url(s3Key) : currentImage?.full || currentImage?.fallback;

  // Initialize Pannellum viewer
  useEffect(() => {
    if (!pannellumContainerRef.current || !imageUrl) return;

    // Destroy previous viewer
    if (viewerRef.current) {
      try {
        viewerRef.current.destroy();
      } catch (e) {}
      viewerRef.current = null;
    }

    setIsLoading(true);
    setError(null);
    fullSwappedRef.current = false;   // new panorama → stage-2 upgrade re-arms
    try { fullViewerRef.current?.destroy(); } catch {}
    fullViewerRef.current = null;
    if (fullContainerRef.current) {
      fullContainerRef.current.style.opacity = '0';
      fullContainerRef.current.style.pointerEvents = 'none';
    }

    // Create viewer using window.pannellum
    try {
      if (!window.pannellum) {
        throw new Error('Pannellum not loaded');
      }

      viewerRef.current = window.pannellum.viewer(pannellumContainerRef.current, {
        type: 'equirectangular',
        panorama: imageUrl,
        autoLoad: true,
        // EV-605: -2.5°/s slow auto-pan; pannellum stops it the moment the user
        // grabs the view (takeover), which is exactly the wanted behaviour.
        autoRotate: autoRotateSpeed,
        // EV-605: instant blurred preview (1400px webp, usually already in the
        // browser cache from the gallery) shown while the 4096px texture loads.
        preview: currentImage?.full || undefined,
        // Patrick 2026-06-08: our overlay is ALREADY a fullscreen modal (inset:0).
        // Pannellum's own browser-fullscreen button put the inner canvas into
        // element-fullscreen ON TOP of our overlay → hid our ✕ close button →
        // user was TRAPPED with no way out. Disable it; ✕ / ESC closes the viewer.
        showFullscreenCtrl: false,
        // EV-605 (Patrick 2026-06-11): the pannellum zoom control panel sat
        // top-left ("something really funny on top") and confused exit.
        // Pinch / mouse-wheel / double-tap zoom all still work without it.
        showZoomCtrl: false,
        showControls: false,
        mouseZoom: true,
        keyboardZoom: true,
        // CON-1376 — THE pan-speed knob. Pannellum multiplies touch drag by this;
        // unset (1) it crawls when zoomed in because drag-degrees scale with hfov.
        touchPanSpeedCoeffFactor: touchPanSpeed,
        doubleClickZoom: true,
        // Cross-fade duration used by loadScene() for the stage-1 → stage-2 swap.
        sceneFadeDuration: fadeMs,
        haov: 360,
        vaov: 180,
        // v69.868 — start zoom is admin-tunable (Patrick: 80 was too zoomed-in);
        // default back to 100 = comfortably wide without fisheye distortion.
        hfov: initialHfov,
        // v69.863 (Patrick): the equirect CENTER (yaw 0) is where his 360 camera's
        // stitch blemish sits — every pano opened staring at the scratch. Start
        // rotated 180° so the opening view is the clean side.
        yaw: 180,
        minHfov: minHfovSetting,
        maxHfov: maxHfovSetting,
      });

      viewerRef.current.on('load', () => {
        setIsLoading(false);

        // ── CON-1376 stage 2, done seamlessly ──────────────────────────────
        // Build the native-res viewer in a SEPARATE stacked layer, keep it
        // invisible until its own 'load' fires, then cross-fade and only THEN
        // dispose of stage 1. The user never sees a reload.
        // Triggered by the first real interaction (Patrick: "as soon as someone
        // is panning or zooming, load the second version in the background"), so
        // a passing glance never pulls a ~3 MB file.
        if (!progressiveFull || !s3Key) return;
        if (getMaxTextureSize() < 8192) return;   // weak GPU — stage 1 is final

        const startStage2 = () => {
          if (fullSwappedRef.current) return;
          fullSwappedRef.current = true;          // one-way, guards re-entry
          const pre = new Image();
          pre.crossOrigin = 'anonymous';
          pre.onerror = () => { fullSwappedRef.current = false; };  // keep stage 1
          pre.onload = () => {
            const a = viewerRef.current;
            const host = fullContainerRef.current;
            if (!a || !host || !window.pannellum) return;
            let keep;
            try { keep = { yaw: a.getYaw(), pitch: a.getPitch(), hfov: a.getHfov() }; }
            catch { return; }
            try {
              fullViewerRef.current = window.pannellum.viewer(host, {
                type: 'equirectangular',
                panorama: pre.src,
                autoLoad: true,
                autoRotate: 0,
                showFullscreenCtrl: false, showZoomCtrl: false, showControls: false,
                mouseZoom: true, keyboardZoom: true,
                touchPanSpeedCoeffFactor: touchPanSpeed,
                doubleClickZoom: true,
                haov: 360, vaov: 180,
                yaw: keep.yaw, pitch: keep.pitch, hfov: keep.hfov,
                minHfov: minHfovSetting, maxHfov: maxHfovSetting,
              });
              // Only once the big texture is actually painted do we reveal it.
              fullViewerRef.current.on('load', () => {
                host.style.opacity = '1';
                host.style.pointerEvents = 'auto';
                setTimeout(() => {
                  try { viewerRef.current?.destroy(); } catch {}
                  viewerRef.current = fullViewerRef.current;
                }, fadeMs);
              });
              fullViewerRef.current.on('error', () => {
                try { fullViewerRef.current?.destroy(); } catch {}
                fullViewerRef.current = null;
                fullSwappedRef.current = false;   // stay on stage 1
              });
            } catch (e) {
              console.warn('[360] stage-2 build failed, staying on stage 1', e);
              fullSwappedRef.current = false;
            }
          };
          pre.src = getFullUrl(s3Key);
        };

        const host = pannellumContainerRef.current;
        if (host) {
          const once = () => {
            host.removeEventListener('pointerdown', once);
            host.removeEventListener('wheel', once);
            startStage2();
          };
          host.addEventListener('pointerdown', once, { passive: true });
          host.addEventListener('wheel', once, { passive: true });
        }
      });

      viewerRef.current.on('error', (err) => {
        console.error('Pannellum error:', err);
        setIsLoading(false);
        setError(t('gallery.failed360Load'));
      });

    } catch (err) {
      console.error('Error initializing Pannellum:', err);
      setIsLoading(false);
      setError(t('gallery.failed360Init'));
    }

    return () => {
      if (viewerRef.current) {
        try {
          viewerRef.current.destroy();
        } catch (e) {}
        viewerRef.current = null;
      }
    };
  }, [imageUrl]);

  // Keyboard & body scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && e.shiftKey) goToPrevious();
      if (e.key === 'ArrowRight' && e.shiftKey) goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < images.length - 1) setCurrentIndex(prev => prev + 1);
  }, [currentIndex, images.length]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  }, [currentIndex]);

  // EV-283 — Share this 360° so it posts to Facebook as a PROPER interactive 360.
  // KEY: we must share the ORIGINAL .jpg (CDN_BASE/s3Key), NOT the resized webp the
  // viewer displays — Facebook detects 360 from the embedded GPano/XMP spherical
  // metadata (UsePanoramaViewer / ProjectionType=equirectangular), which our
  // originals keep but resizing/webp strips. Web Share API hands the actual file to
  // the FB app, which uploads it and auto-renders the 360. Desktop / unsupported →
  // download the original + tell the user to upload it to FB (same result).
  const [sharing, setSharing] = useState(false);
  const share360 = useCallback(async () => {
    if (!s3Key || sharing) return;
    setSharing(true);
    try {
      const res = await fetch(`${CDN_BASE}/${s3Key}`);
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      const blob = await res.blob();
      const fname = `${(curVenueName || 'venue').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'venue'}-360.jpg`;
      const file = new File([blob], fname, { type: 'image/jpeg' });
      const shareText = t('gallery.share360Text', { name: curVenueName });
      if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: t('gallery.venue360Title', { name: venueName }), text: shareText });
      } else {
        // Desktop / no file-share: download the original + instruct.
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl; a.download = fname;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
        alert(t('gallery.download360Instructions'));
      }
    } catch (e) {
      if (e && e.name === 'AbortError') { /* user cancelled the share sheet */ }
      else { console.error('[360 share] failed', e); alert(t('gallery.share360Failed')); }
    } finally {
      setSharing(false);
    }
  }, [s3Key, venueName, sharing]);

  // EV-291 — Share a LINK to this exact 360 (deep link → our fullscreen viewer).
  // This is the one to send to WhatsApp/IG/Telegram/etc: it stays an INTERACTIVE
  // 360 on our site everywhere (vs the raw file, which only Facebook unwraps), and
  // it credits HonestLee. Web Share when available, clipboard copy otherwise.
  const shareLink = async () => {
    const key = currentImage?.s3Key;
    // EV-292 — share the OG-prefixed URL (core.honestlee.app/og/360/...) so
    // WhatsApp/FB crawlers get a preview image + title; real users meta-refresh
    // into the interactive viewer at vn.honestlee.app/360/<slug>.
    const url = `https://core.honestlee.app/og/360/${curVenueSlug || ''}${key ? `?img=${encodeURIComponent(key)}` : ''}`;
    const data = { title: t('gallery.venue360Title', { name: curVenueName }), text: t('gallery.share360LinkText', { name: curVenueName }), url };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) { await navigator.share(data); return; }
      await navigator.clipboard.writeText(url);
      window.alert(t('gallery.linkCopiedPasteAnywhere'));
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      try { await navigator.clipboard.writeText(url); window.alert(t('common.linkCopied')); } catch { /* noop */ }
    }
  };

  return createPortal(
    <div ref={containerRef} style={styles.overlay} data-testid="panellum-360-viewer">
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.counter}>{currentIndex + 1} / {images.length}</span>
          <span style={styles.title}>{t('gallery.venue360Title', { name: curVenueName })}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {/* EV-605: share buttons are ICON-ONLY — the old wide text buttons
              ("Share link" + "Facebook 360°") overflowed the header on phones
              and pushed the ✕ close button OFF-SCREEN → Patrick was trapped
              in the viewer with no visible exit. */}
          {/* EV-291 — Share LINK (deep link to this 360) — for WhatsApp/IG/etc. */}
          {curVenueSlug && (
            <button
              onClick={shareLink}
              style={{ ...styles.shareLinkButton }}
              title={t('gallery.shareLinkTooltip')}
            >
              <span style={{ fontSize: 17, lineHeight: 1 }}>🔗</span>
            </button>
          )}
          {/* EV-283 — Share the FILE → Facebook renders it as an interactive 360° */}
          <button
            onClick={share360}
            disabled={sharing || !s3Key}
            style={{ ...styles.shareButton, opacity: (sharing || !s3Key) ? 0.6 : 1 }}
            title={t('gallery.shareFacebookTooltip')}
          >
            <span style={{ fontSize: 17, lineHeight: 1 }}>{sharing ? '⏳' : '📤'}</span>
          </button>
          <button onClick={onClose} style={styles.closeButton} title={t('gallery.close360Tooltip')} aria-label={t('gallery.close360AriaLabel')}>
            <XMarkIcon style={{ width: 26, height: 26 }} />
          </button>
        </div>
      </div>

      {/* Pannellum Container */}
      <div style={styles.viewerWrapper}>
        {isLoading && (
          <div style={styles.loadingOverlay}>
            <div style={styles.spinner} />
            <span style={styles.loadingText}>{t('gallery.loading360View')}</span>
          </div>
        )}
        {error && (
          <div style={styles.errorOverlay}>
            <span style={styles.errorText}>{error}</span>
          </div>
        )}
        <div ref={pannellumContainerRef} style={styles.pannellum} />
        {/* CON-1376 fix — stage-2 lives in its OWN layer stacked on top, created
            invisible and faded in only once it has finished loading. The first
            implementation destroyed and rebuilt the single viewer, which read as
            the panorama loading twice (Patrick 2026-07-26). Nothing is torn down
            until the replacement is fully painted. */}
        <div
          ref={fullContainerRef}
          style={{
            ...styles.pannellum,
            position: 'absolute', inset: 0,
            opacity: 0, pointerEvents: 'none',
            transition: `opacity ${fadeMs}ms ease`,
          }}
        />
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            style={{ ...styles.navButton, left: 20, opacity: currentIndex === 0 ? 0.3 : 1 }}
          >
            <ChevronLeftIcon style={{ width: 32, height: 32 }} />
          </button>
          <button
            onClick={goToNext}
            disabled={currentIndex === images.length - 1}
            style={{ ...styles.navButton, right: 20, opacity: currentIndex === images.length - 1 ? 0.3 : 1 }}
          >
            <ChevronRightIcon style={{ width: 32, height: 32 }} />
          </button>
        </>
      )}

      {/* Thumbnails */}
      {images.length > 1 && (
        <div style={styles.thumbnailStrip}>
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => setCurrentIndex(idx)}
              style={{
                ...styles.thumbnail,
                border: idx === currentIndex ? '2px solid #3b82f6' : '2px solid transparent',
              }}
            >
              <img src={img.thumb || img.lightboxThumb} alt="" style={styles.thumbImg} />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#000',
    zIndex: 9999,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    // EV-605: respect the notch — the close ✕ was partly under the iOS status
    // bar making it hard to tap ("can't get out of the 360 viewer").
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)',
    paddingBottom: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 16,
    paddingRight: 12,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
    zIndex: 10,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 },
  counter: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    backgroundColor: 'rgba(0,0,0,0.5)',
    padding: '4px 12px',
    borderRadius: 20,
  },
  title: { color: '#fff', fontSize: 16, fontWeight: 500 },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    border: 'none',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // EV-283 — Share to Facebook button (brand orange — primary action)
  shareButton: {
    height: 40,
    padding: '0 14px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#E46E34',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  // EV-291 — Share link button (secondary — translucent)
  shareLinkButton: {
    height: 40,
    padding: '0 12px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.35)',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  viewerWrapper: {
    position: 'absolute',
    top: 56,
    left: 0,
    right: 0,
    bottom: 80,
  },
  pannellum: {
    width: '100%',
    height: '100%',
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.8)',
    zIndex: 5,
  },
  spinner: {
    width: 48,
    height: 48,
    border: '4px solid rgba(255,255,255,0.2)',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: { color: '#fff', marginTop: 16, fontSize: 14 },
  errorOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.8)',
    zIndex: 5,
  },
  errorText: { color: '#ef4444', fontSize: 16 },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 56,
    height: 56,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10,
  },
  thumbnailStrip: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 20px',
    backgroundColor: 'rgba(0,0,0,0.8)',
    overflowX: 'auto',
    zIndex: 10,
  },
  thumbnail: {
    width: 56,
    height: 56,
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    flexShrink: 0,
    padding: 0,
    background: 'none',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
};

// Add spinner animation
if (typeof document !== 'undefined' && !document.querySelector('style[data-pano-spin]')) {
  const s = document.createElement('style');
  s.setAttribute('data-pano-spin', 'true');
  s.textContent = '@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
  document.head.appendChild(s);
}

export default Panellum360Viewer;
